<script setup lang="ts">
import type { ResultsSearchResultProperties, SearchResult } from '@optionway/models';
import { Info, Award, Tag, Rocket, Wind, CircleAlert, ChevronDown } from 'lucide-vue-next';

const { $appConfig } = useNuxtApp();

const config = $appConfig.app?.components?.resultsSearchResult;

const { loading } = useSearchResultsQueryState();
const { loading: carbonLoading } = useCarbonEmissionQueryState();

// The card is one of N rows: it does not price, it asks for it. The list owns the single pricing
// mutation and tells each row whether IT is being priced (pricing) or ANOTHER one is (selectDisabled).
const properties = defineProps<
  ResultsSearchResultProperties & SearchResult & { pricing?: boolean; selectDisabled?: boolean }
>();
const emit = defineEmits<{ select: [offerUuid: string] }>();

const prefix = 'components.results.searchResult';

const searchResultIndexStore = useSearchResultIndexStore();
// This offer's CO₂ is still on its way: the query is loading AND this offer hasn't been answered yet.
const offerPending = computed(
  () => carbonLoading.value && searchResultIndexStore.offerUuidsMissingCarbon.includes(properties.offerUuid),
);

const componentConfig = computed<ResultsSearchResultProperties>(() => {
  return {
    components: properties.components ?? config?.components,
    hideComponents: properties.hideComponents ?? config?.hideComponents,
  };
});

const select = () => {
  emit('select', properties.offerUuid);
};

const detailsOpen = ref(false);
const detailsLegId = ref<number>();

const showDetails = (legId?: number) => {
  detailsLegId.value = legId;
  detailsOpen.value = true;
};

const locationLabelsStore = useLocationLabels();
</script>

<template>
  <UiCard
    role="article"
    :aria-label="
      !loading && properties.legs?.length
        ? `${locationLabelsStore.city(properties.legs[0]?.departure?.cityIataCode) ?? properties.legs[0]?.departure?.iataCode} → ${locationLabelsStore.city(properties.legs.at(-1)?.arrival?.cityIataCode) ?? properties.legs.at(-1)?.arrival?.iataCode}, ${usePriceFormat(properties.price?.averagePerPax)}`
        : undefined
    "
    :class="
      cn(
        'mx-auto flex w-full flex-col justify-start gap-1 self-stretch rounded-2xl p-0 shadow-md md:gap-2 md:p-2',
        $attrs.class ?? '',
      )
    "
  >
    <UiCardHeader class="items-left inline-flex justify-start gap-4 px-4 pb-0 pt-4 md:p-0">
      <div class="inline-flex items-center justify-start gap-2">
        <UiSkeleton
          v-if="loading"
          class="h-6 w-24"
        />
        <div
          v-else
          class="flex flex-wrap items-center gap-1"
        >
          <!-- Transport / airport-change tags -->
          <UiCustomBadge
            v-if="properties.equipmentTypes?.includes('BUS')"
            variant="dark-blue"
            radius="m"
            size="s"
          >
            {{ $t(`${prefix}.flightPlusBus`) }}
          </UiCustomBadge>
          <UiCustomBadge
            v-if="properties.equipmentTypes?.includes('TRAIN')"
            variant="dark-blue"
            radius="m"
            size="s"
          >
            {{ $t(`${prefix}.flightPlusTrain`) }}
          </UiCustomBadge>
          <UiCustomBadge
            v-if="properties.changeType === 'AIRPORT'"
            variant="orange"
            radius="m"
            size="s"
          >
            <CircleAlert
              :size="12"
              aria-hidden="true"
            />
            {{ $t(`${prefix}.airportChangeBadge`) }}
          </UiCustomBadge>

          <!-- Sort badges -->
          <UiCustomBadge
            v-if="properties?.badges?.bestOffer"
            variant="teal"
            radius="m"
            size="s"
          >
            <Award
              :size="12"
              aria-hidden="true"
            />
            <span class="md:hidden">{{ $t('components.results.sortOptions.badgeLabel.bestOffer') }}</span>
            <span class="hidden md:inline">{{ $t('components.results.sortOptions.bestOffer') }}</span>
          </UiCustomBadge>
          <UiCustomBadge
            v-if="properties?.badges?.cheapest"
            variant="purple"
            radius="m"
            size="s"
          >
            <Tag
              :size="12"
              aria-hidden="true"
            />
            <span class="md:hidden">{{ $t('components.results.sortOptions.badgeLabel.price') }}</span>
            <span class="hidden md:inline">{{ $t('components.results.sortOptions.price') }}</span>
          </UiCustomBadge>
          <UiCustomBadge
            v-if="properties?.badges?.fastest"
            variant="burgundy"
            radius="m"
            size="s"
          >
            <Rocket
              :size="12"
              aria-hidden="true"
            />
            <span class="md:hidden">{{ $t('components.results.sortOptions.badgeLabel.duration') }}</span>
            <span class="hidden md:inline">{{ $t('components.results.sortOptions.duration') }}</span>
          </UiCustomBadge>
          <UiCustomBadge
            v-if="properties?.badges?.carbonEmission"
            variant="green"
            radius="m"
            size="s"
          >
            <Wind
              :size="12"
              aria-hidden="true"
            />
            <span class="md:hidden">{{ $t('components.results.sortOptions.badgeLabel.carbonEmission') }}</span>
            <span class="hidden md:inline">{{ $t('components.results.sortOptions.carbonEmission') }}</span>
          </UiCustomBadge>
          <!-- Carbon emission tag -->
          <template v-if="properties?.carbonEmission">
            <!-- Mobile: bottom sheet -->
            <UiCustomBottomSheet
              :title="$t('components.results.carbonEmissionTooltip.title')"
              :close-label="$t(`${prefix}.close`)"
              trigger-class="md:hidden"
            >
              <template #trigger>
                <UiCustomBadge
                  variant="green"
                  radius="m"
                  size="s"
                  appearance="outline"
                  class="cursor-pointer"
                >
                  <i18n-t
                    scope="global"
                    :keypath="`${prefix}.carbonEmissionAmount`"
                  >
                    <template #amount>{{ useCarbonEmissionFormat(properties.carbonEmission) }}</template>
                  </i18n-t>
                  <Info
                    :size="12"
                    aria-hidden="true"
                  />
                </UiCustomBadge>
              </template>
              <ResultsCarbonEmissionTooltip />
            </UiCustomBottomSheet>
            <!-- Desktop: tooltip -->
            <UiTooltip>
              <UiTooltipTrigger
                as-child
                class="hidden md:inline-flex"
              >
                <UiCustomBadge
                  variant="green"
                  radius="m"
                  size="s"
                  appearance="outline"
                  class="cursor-pointer"
                >
                  <i18n-t
                    scope="global"
                    :keypath="`${prefix}.carbonEmissionAmount`"
                  >
                    <template #amount>{{ useCarbonEmissionFormat(properties.carbonEmission) }}</template>
                  </i18n-t>
                  <Info
                    :size="12"
                    aria-hidden="true"
                  />
                </UiCustomBadge>
              </UiTooltipTrigger>
              <UiTooltipContent class="max-w-80">
                <ResultsCarbonEmissionTooltip dark />
              </UiTooltipContent>
            </UiTooltip>
          </template>
          <!-- CO₂ still being fetched for this offer → skeleton ; settled without CO₂ → empty badge -->
          <UiSkeleton
            v-else-if="offerPending"
            class="h-6 w-24 rounded-md"
          />
          <UiCustomBadge
            v-else
            variant="gray"
            state="disabled"
            radius="m"
            size="s"
            appearance="outline"
          >
            {{ $t(`${prefix}.carbonEmissionEmpty`) }}
          </UiCustomBadge>
        </div>
      </div>
    </UiCardHeader>
    <UiCardContent>
      <div class="grid grid-cols-result-card grid-rows-auto-2 gap-2 px-4 md:px-0">
        <!-- Col 1, Row 1: Legs + alerts -->
        <div
          v-if="properties?.legs"
          class="col-span-2 flex flex-col space-y-3 md:col-span-1"
        >
          <div
            v-for="(leg, index) in properties.legs"
            :key="index"
            class="flex h-full flex-col"
            :class="{ 'border-b border-gray-300 pb-2 md:border-b-0 md:pb-0': index !== properties.legs.length - 1 }"
          >
            <!-- pb-2 instead of gap-2 on the parent keeps the strip above the chevron inside the tap target -->
            <div
              role="button"
              tabindex="0"
              aria-haspopup="dialog"
              class="flex h-full cursor-pointer flex-col pb-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background md:rounded-xl md:pb-0 md:transition-colors md:hover:bg-gray-50"
              @click="showDetails(leg.id)"
              @keydown.enter.prevent="showDetails(leg.id)"
              @keydown.space.prevent
              @keyup.space.prevent="showDetails(leg.id)"
            >
              <ResultsLeg
                v-if="leg"
                v-bind="leg"
                :loading="loading"
                :hide-pin="componentConfig.components?.resultsLeg?.hidePin ?? false"
                :hide-bags="componentConfig.components?.resultsLeg?.hideBags ?? false"
                :hide-date="componentConfig.components?.resultsLeg?.hideDate ?? false"
                hide-border-bottom
                class="h-full"
              />
            </div>
            <UiSkeleton
              v-if="loading"
              class="mx-auto h-4 w-4 md:hidden"
            />
            <button
              v-else
              type="button"
              aria-haspopup="dialog"
              :aria-label="$t('components.results.leg.openDetails')"
              class="flex w-full items-center justify-center md:hidden"
              @click="showDetails(leg.id)"
            >
              <ChevronDown
                class="h-4 w-4 text-text-secondary-600"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div v-else>No Leg</div>

        <!-- Col 2, Row 1 (desktop only): Price -->
        <div class="col-start-2 row-start-1 hidden md:block">
          <ResultsPriceSelect
            v-if="properties?.price"
            v-bind="properties.price"
            :pricing="properties.pricing"
            :loading="loading"
            :disabled="properties.selectDisabled"
            @select-result="select"
          />
          <span v-else>{{ $t(`${prefix}.noPrice`) }}</span>
        </div>

        <!-- Col 1, Row 2 (desktop only): Voir détails -->
        <div
          v-if="properties?.legs"
          class="col-start-1 hidden h-full md:flex md:items-start md:justify-center"
        >
          <UiSkeleton
            v-if="loading"
            class="h-full min-h-5 w-full max-w-20"
          />
          <UiButton
            v-else
            aria-haspopup="dialog"
            variant="tertiary"
            color="gray"
            padding="none"
            class="h-full w-full justify-center font-normal"
            @click="showDetails()"
          >
            {{ $t(`${prefix}.details`) }}
          </UiButton>
        </div>
      </div>
      <!-- Row 2 (mobile only): Price row -->
      <div
        v-if="properties?.price && properties?.legs"
        class="border-t border-border px-4 py-4 md:hidden"
      >
        <ResultsPriceRow
          v-bind="properties.price"
          :pricing="properties.pricing"
          :loading="loading"
          :disabled="properties.selectDisabled"
          @select-result="select"
        />
      </div>
    </UiCardContent>

    <ResultsFlightDetails
      v-if="properties?.legs"
      v-model:open="detailsOpen"
      :legs="properties.legs"
      :price="properties.price"
      :loading="properties.pricing || properties.selectDisabled"
      :trip-type="properties.tripType"
      :initial-leg-id="detailsLegId"
      @buy="select"
    />
  </UiCard>
</template>
